import { useState, useEffect } from "react";
import { useNossaHistoria, NossaHistoriaContent } from "@/hooks/useNossaHistoria";
import { settingsApi } from "@/integrations/mysql/client";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Loader2, Save, FileText, BookOpen, Sparkles, ChevronDown, Plus, Trash2,
  Heart, Target, Users, Brain, Leaf, Star, Shield, Award, Zap,
  Apple, Clock, Globe, CheckCircle, Handshake, type LucideIcon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PhotoCropUpload } from "@/components/ui/PhotoCropUpload";

// ── Principle icon map ─────────────────────────────────────────────────────────
const PRINCIPLE_ICON_MAP: Record<string, LucideIcon> = {
  Heart, Target, Users, Brain, Leaf, Star, Shield, Award, Zap,
  Apple, Clock, Globe, CheckCircle, Handshake, Sparkles,
};

function PrincipleIconPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const SelectedIcon = PRINCIPLE_ICON_MAP[value] || Heart;
  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-800 bg-white hover:border-brand-primary/50 transition-colors w-full text-left"
      >
        <SelectedIcon className="w-4 h-4 text-brand-primary shrink-0" />
        <span className="flex-1 truncate">{value || "Selecionar ícone"}</span>
        <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      </button>
      {open && (
        <div className="border border-gray-200 rounded-xl p-2 bg-white shadow-sm">
          <div className="grid grid-cols-5 gap-1">
            {Object.entries(PRINCIPLE_ICON_MAP).map(([name, Icon]) => (
              <button
                key={name}
                type="button"
                onClick={() => { onChange(name); setOpen(false); }}
                title={name}
                className={`flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-lg transition-all ${
                  value === name ? "bg-brand-primary text-white" : "hover:bg-brand-primary/8 text-gray-600 hover:text-brand-primary"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="text-[8px] font-medium leading-tight truncate w-full text-center">{name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">{label}</Label>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength }: {
  value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
    />
  );
}

function TextArea({ value, onChange, placeholder, rows = 3, maxLength }: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; maxLength?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all resize-none"
    />
  );
}

function SaveBar({ dirty, saving, onSave }: { dirty: boolean; saving: boolean; onSave: () => void }) {
  if (!dirty) return null;
  return (
    <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3 mt-4">
      <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
      <button
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Salvar
      </button>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="flex items-center gap-3 pb-3 border-b border-gray-100 mb-4">
      <div className="w-8 h-8 rounded-lg bg-brand-primary/8 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-brand-primary" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        <p className="text-xs text-gray-400">{description}</p>
      </div>
    </div>
  );
}

// ─── Section-level editors ─────────────────────────────────────────────────

// Hero section
function HeroEditor({ content, onSave }: {
  content: NossaHistoriaContent[] | undefined;
  onSave: (section: string, key: string, value: Record<string, unknown>, existingId?: string) => Promise<void>;
}) {
  const find = (key: string) => content?.find(c => c.section === 'hero' && c.content_key === key);
  const titleItem    = find('title');
  const subtitleItem = find('subtitle');
  const imageItem    = find('image');

  const DEFAULT_TITLE    = 'Quem Somos';
  const DEFAULT_SUBTITLE = 'Conheça a jornada da Dra Brenda Ramos e sua missão de transformar vidas através da nutrição';

  const [title, setTitle]       = useState((titleItem?.content_value?.text as string) || DEFAULT_TITLE);
  const [subtitle, setSubtitle] = useState((subtitleItem?.content_value?.text as string) || DEFAULT_SUBTITLE);
  const [saving, setSaving]     = useState(false);

  useEffect(() => { setTitle((titleItem?.content_value?.text as string) || DEFAULT_TITLE); }, [titleItem?.id]);
  useEffect(() => { setSubtitle((subtitleItem?.content_value?.text as string) || DEFAULT_SUBTITLE); }, [subtitleItem?.id]);

  const origTitle    = (titleItem?.content_value?.text as string) || DEFAULT_TITLE;
  const origSubtitle = (subtitleItem?.content_value?.text as string) || DEFAULT_SUBTITLE;
  const dirty = title !== origTitle || subtitle !== origSubtitle;

  const handleSave = async () => {
    setSaving(true);
    await Promise.all([
      onSave('hero', 'title',    { text: title },    titleItem?.id),
      onSave('hero', 'subtitle', { text: subtitle }, subtitleItem?.id),
    ]);
    setSaving(false);
  };

  const handleImageChange = async (url: string) => {
    await onSave('hero', 'image', { url }, imageItem?.id);
  };

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={Sparkles} title="Cabeçalho (Hero)" description="Texto e imagem exibidos no topo verde da página Quem Somos" />

      <Field label="Imagem de destaque" hint="Aparece no lado direito da seção — recomendado quadrado (1:1)">
        <div className="mt-1">
          <PhotoCropUpload
            value={(imageItem?.content_value?.url as string) ?? ''}
            onChange={handleImageChange}
            shape="rect"
            aspect={1}
            label="Imagem hero"
            previewClassName="w-24 h-24 rounded-xl"
          />
        </div>
      </Field>

      <div className="border-t border-gray-100 pt-4 space-y-4">
        <Field label={`Título (${title.length}/200)`} hint="Título grande em destaque">
          <TextInput value={title} onChange={setTitle} placeholder="Ex: Quem Somos" maxLength={200} />
        </Field>
        <Field label={`Subtítulo (${subtitle.length}/400)`} hint="Frase abaixo do título">
          <TextArea value={subtitle} onChange={setSubtitle} placeholder="Ex: Conheça a jornada da Dra. Brenda Ramos..." rows={3} maxLength={400} />
        </Field>
        <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
      </div>
    </div>
  );
}

// Story section
function StoryEditor({ content, onSave }: {
  content: NossaHistoriaContent[] | undefined;
  onSave: (section: string, key: string, value: Record<string, unknown>, existingId?: string) => Promise<void>;
}) {
  const find = (key: string) => content?.find(c => c.section === 'story' && c.content_key === key);
  const titleItem    = find('title');
  const crnItem      = find('crn');
  const crnLabelItem = find('crn_label');

  const DEFAULT_TITLE     = 'Uma Jornada de Dedicação e Propósito';
  const DEFAULT_CRN       = '3/63492';
  const DEFAULT_CRN_LABEL = 'Nutricionista registrada no Conselho Regional de Nutricionistas';

  const [title,    setTitle]    = useState((titleItem?.content_value?.text    as string) || DEFAULT_TITLE);
  const [crn,      setCrn]      = useState((crnItem?.content_value?.text      as string) || DEFAULT_CRN);
  const [crnLabel, setCrnLabel] = useState((crnLabelItem?.content_value?.text as string) || DEFAULT_CRN_LABEL);
  const [saving, setSaving]     = useState(false);

  useEffect(() => { setTitle((titleItem?.content_value?.text       as string) || DEFAULT_TITLE); },     [titleItem?.id]);
  useEffect(() => { setCrn((crnItem?.content_value?.text           as string) || DEFAULT_CRN); },      [crnItem?.id]);
  useEffect(() => { setCrnLabel((crnLabelItem?.content_value?.text as string) || DEFAULT_CRN_LABEL); },[crnLabelItem?.id]);

  const dirty =
    title    !== ((titleItem?.content_value?.text    as string) || DEFAULT_TITLE)     ||
    crn      !== ((crnItem?.content_value?.text      as string) || DEFAULT_CRN)       ||
    crnLabel !== ((crnLabelItem?.content_value?.text as string) || DEFAULT_CRN_LABEL);

  const handleSave = async () => {
    setSaving(true);
    await Promise.all([
      onSave('story', 'title',     { text: title },    titleItem?.id),
      onSave('story', 'crn',       { text: crn },      crnItem?.id),
      onSave('story', 'crn_label', { text: crnLabel }, crnLabelItem?.id),
    ]);
    setSaving(false);
  };

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={BookOpen} title="História" description="Seção com texto e imagem sobre a trajetória da nutricionista" />
      <Field label={`Título da seção (${title.length}/200)`} hint="Ex: Uma Jornada de Dedicação e Propósito">
        <TextInput value={title} onChange={setTitle} placeholder={DEFAULT_TITLE} maxLength={200} />
      </Field>
      <div className="border border-gray-100 rounded-xl p-4 space-y-3 bg-gray-50">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Card de CRN</p>
        <Field label="Número do CRN" hint="Ex: 3/63492">
          <TextInput value={crn} onChange={setCrn} placeholder={DEFAULT_CRN} maxLength={30} />
        </Field>
        <Field label={`Subtítulo do CRN (${crnLabel.length}/200)`} hint="Texto abaixo do número de registro">
          <TextInput value={crnLabel} onChange={setCrnLabel} placeholder={DEFAULT_CRN_LABEL} maxLength={200} />
        </Field>
      </div>
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// CTA section editor (bottom of page)
function CTAEditor() {
  const DEFAULT_CTA = {
    title: "Pronto para Iniciar sua Jornada?",
    description: "Agende uma consulta e descubra como podemos transformar sua relação com a alimentação",
    buttonText: "Falar com a Doutora",
    buttonUrl: "https://wa.me/5511998874081",
  };

  const [title, setTitle]       = useState(DEFAULT_CTA.title);
  const [description, setDesc]  = useState(DEFAULT_CTA.description);
  const [buttonText, setBtnText] = useState(DEFAULT_CTA.buttonText);
  const [buttonUrl, setBtnUrl]   = useState(DEFAULT_CTA.buttonUrl);
  const [orig, setOrig]          = useState(DEFAULT_CTA);
  const [saving, setSaving]      = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    settingsApi.list().then((list: any[]) => {
      const raw = list.find((s: any) => s.key === 'nossa_historia_cta')?.value;
      if (raw) {
        try {
          const p = JSON.parse(raw);
          setTitle(p.title || DEFAULT_CTA.title);
          setDesc(p.description || DEFAULT_CTA.description);
          setBtnText(p.buttonText || DEFAULT_CTA.buttonText);
          setBtnUrl(p.buttonUrl || DEFAULT_CTA.buttonUrl);
          setOrig(p);
        } catch {}
      }
    }).catch(() => {});
  }, []);

  const dirty =
    title !== (orig.title || DEFAULT_CTA.title) ||
    description !== (orig.description || DEFAULT_CTA.description) ||
    buttonText !== (orig.buttonText || DEFAULT_CTA.buttonText) ||
    buttonUrl !== (orig.buttonUrl || DEFAULT_CTA.buttonUrl);

  const handleSave = async () => {
    setSaving(true);
    const payload = { title, description, buttonText, buttonUrl };
    await settingsApi.update('nossa_historia_cta', JSON.stringify(payload));
    setOrig(payload);
    toast({ title: "CTA salvo!" });
    setSaving(false);
  };

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={Sparkles} title="CTA (Chamada para Ação)" description="Seção verde ao final da página Quem Somos" />
      <Field label={`Título (${title.length}/150)`}>
        <TextInput value={title} onChange={setTitle} placeholder="Ex: Pronto para Iniciar sua Jornada?" maxLength={150} />
      </Field>
      <Field label={`Descrição (${description.length}/400)`}>
        <TextArea value={description} onChange={setDesc} placeholder="Ex: Agende uma consulta..." rows={3} maxLength={400} />
      </Field>
      <Field label={`Texto do botão (${buttonText.length}/60)`}>
        <TextInput value={buttonText} onChange={setBtnText} placeholder="Ex: Falar com a Doutora" maxLength={60} />
      </Field>
      <Field label="Link do botão" hint="URL do WhatsApp ou outra página">
        <TextInput value={buttonUrl} onChange={setBtnUrl} placeholder="https://wa.me/55..." />
      </Field>
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Principles constants ───────────────────────────────────────────────────────
const PRINCIPLE_KEYS    = ['p1', 'p2', 'p3', 'p4', 'p5'] as const;
const LEGACY_KEYS       = ['humanization', 'sustainability', 'evidence'];
const ORDINALS          = ['Primeiro', 'Segundo', 'Terceiro', 'Quarto', 'Quinto'];
const DEFAULT_P_ICONS   = ['Heart', 'Target', 'Users', 'Brain', 'Leaf'];

// Principle editor (one card)
function PrincipleCard({ label, contentKey, item, onSave, defaultIcon = 'Heart', isHighlighted = false, onDelete }: {
  label: string;
  contentKey: string;
  item: NossaHistoriaContent | undefined;
  onSave: (section: string, key: string, value: Record<string, unknown>, existingId?: string) => Promise<void>;
  defaultIcon?: string;
  isHighlighted?: boolean;
  onDelete?: () => void;
}) {
  const [title, setTitle]      = useState((item?.content_value?.title as string) ?? '');
  const [description, setDesc] = useState((item?.content_value?.description as string) ?? '');
  const [icon, setIcon]        = useState((item?.content_value?.icon as string) || defaultIcon);
  const [saving, setSaving]    = useState(false);

  useEffect(() => { setTitle((item?.content_value?.title as string) ?? ''); }, [item?.id]);
  useEffect(() => { setDesc((item?.content_value?.description as string) ?? ''); }, [item?.id]);
  useEffect(() => { setIcon((item?.content_value?.icon as string) || defaultIcon); }, [item?.id]);

  const origTitle = (item?.content_value?.title as string) ?? '';
  const origDesc  = (item?.content_value?.description as string) ?? '';
  const origIcon  = (item?.content_value?.icon as string) || defaultIcon;
  const dirty = title !== origTitle || description !== origDesc || icon !== origIcon;

  const handleSave = async () => {
    setSaving(true);
    await onSave('principle', contentKey, { title, description, icon }, item?.id);
    setSaving(false);
  };

  return (
    <div className={`border rounded-xl p-4 space-y-3 transition-all ${dirty ? "border-brand-primary/30 bg-brand-primary/2" : "border-gray-100 bg-white"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-bold text-gray-700">{label}</h4>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
            isHighlighted ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-500'
          }`}>
            {isHighlighted ? '● Verde' : '○ Branco'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" title="Alterações não salvas" />}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-1 text-red-400 hover:text-red-600 rounded transition-colors"
              title="Excluir princípio"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <Field label="Ícone" hint="Ícone exibido no card do princípio">
        <PrincipleIconPicker value={icon} onChange={setIcon} />
      </Field>
      <Field label={`Título (${title.length}/100)`}>
        <TextInput value={title} onChange={setTitle} placeholder={`Ex: ${label}`} maxLength={100} />
      </Field>
      <Field label={`Descrição (${description.length}/400)`}>
        <TextArea value={description} onChange={setDesc} placeholder="Descreva este princípio..." rows={3} maxLength={400} />
      </Field>
      <div className="flex justify-end pt-1">
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Salvar
        </button>
      </div>
    </div>
  );
}

function PrinciplesEditor({ content, onSave }: {
  content: NossaHistoriaContent[] | undefined;
  onSave: (section: string, key: string, value: Record<string, unknown>, existingId?: string) => Promise<void>;
}) {
  const findByKey = (key: string) =>
    content?.find(c => c.section === 'principle' && c.content_key === key);

  const getItem = (index: number) =>
    findByKey(PRINCIPLE_KEYS[index]) ?? (index < LEGACY_KEYS.length ? findByKey(LEGACY_KEYS[index]) : undefined);

  const existingCount = PRINCIPLE_KEYS.reduce((n, _, i) => (getItem(i) ? i + 1 : n), 0);
  const [count, setCount] = useState(Math.max(existingCount, 3));
  const [highlightedKey, setHighlightedKey] = useState<string>('p1');
  const [savingHighlight, setSavingHighlight] = useState(false);
  const [disabledKeys, setDisabledKeys] = useState<Set<string>>(new Set());

  // Load highlighted key and disabled keys from settings
  useEffect(() => {
    settingsApi.list().then((list: any[]) => {
      const hVal = list.find((s: any) => s.key === 'principle_highlighted_key')?.value;
      if (hVal) setHighlightedKey(hVal);
      const dRaw = list.find((s: any) => s.key === 'disabled_principle_keys')?.value;
      if (dRaw) {
        try { setDisabledKeys(new Set(JSON.parse(dRaw))); } catch {}
      }
    }).catch(() => {});
  }, []);

  // Keep count in sync if content loads after first render
  useEffect(() => {
    const ec = PRINCIPLE_KEYS.reduce((n, _, i) => (getItem(i) ? i + 1 : n), 0);
    setCount(c => Math.max(c, Math.max(ec, 3)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.length]);

  const handleSetHighlight = async (key: string) => {
    if (key === highlightedKey) return;
    setSavingHighlight(true);
    setHighlightedKey(key);
    await settingsApi.update('principle_highlighted_key', key);
    setSavingHighlight(false);
  };

  const handleDelete = async (key: string) => {
    const newDisabled = new Set([...disabledKeys, key]);
    setDisabledKeys(newDisabled);
    await settingsApi.update('disabled_principle_keys', JSON.stringify([...newDisabled]));
    // If deleted key was highlighted, reset highlight to first visible
    if (highlightedKey === key) {
      const firstValid = PRINCIPLE_KEYS.find((k, i) => !newDisabled.has(k) && (getItem(i) || i < 3));
      if (firstValid) await handleSetHighlight(firstValid);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader icon={FileText} title="Princípios" description="Pilares exibidos como cards na página (até 5)" />

      {/* Highlighted key selector */}
      <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 space-y-2">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Card com fundo verde</p>
        <div className="flex gap-2 flex-wrap">
          {Array.from({ length: count }, (_, i) => i)
            .filter(i => !disabledKeys.has(PRINCIPLE_KEYS[i]))
            .map((i) => {
              const key = PRINCIPLE_KEYS[i];
              const isActive = highlightedKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={savingHighlight}
                  onClick={() => handleSetHighlight(key)}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border-2 transition-all ${
                    isActive
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-brand-primary/40 hover:text-brand-primary'
                  }`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full border-2 ${isActive ? 'bg-white border-white' : 'border-gray-400'}`} />
                  {ORDINALS[i]} Princípio
                </button>
              );
            })}
        </div>
        <p className="text-xs text-gray-400">Clique para definir qual card terá o fundo verde no site público</p>
      </div>

      {Array.from({ length: count }, (_, i) => i)
        .filter(i => !disabledKeys.has(PRINCIPLE_KEYS[i]))
        .map((i) => (
          <PrincipleCard
            key={PRINCIPLE_KEYS[i]}
            label={`${ORDINALS[i]} Princípio`}
            contentKey={PRINCIPLE_KEYS[i]}
            item={getItem(i)}
            onSave={onSave}
            defaultIcon={DEFAULT_P_ICONS[i] || 'Heart'}
            isHighlighted={highlightedKey === PRINCIPLE_KEYS[i]}
            onDelete={() => handleDelete(PRINCIPLE_KEYS[i])}
          />
        ))}
      {count < 5 && (
        <button
          type="button"
          onClick={() => setCount(c => c + 1)}
          className="w-full border-2 border-dashed border-brand-primary/30 rounded-xl py-3 text-sm text-brand-primary/70 hover:border-brand-primary/60 hover:text-brand-primary font-medium transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Adicionar Princípio
        </button>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

export function ContentManager({ section }: { section?: "hero" | "story" | "principles" | "cta" }) {
  const { content, contentLoading, updateContentAsync, createContent } = useNossaHistoria();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<"hero" | "story" | "principles" | "cta">("hero");

  // Upsert: update if record exists, create if not
  const upsave = async (
    section: string,
    key: string,
    value: Record<string, unknown>,
    existingId?: string,
  ) => {
    if (existingId) {
      await updateContentAsync({ id: existingId, updates: { content_value: value } });
    } else {
      await createContent({
        page: 'nossa_historia' as const,
        section: section as NossaHistoriaContent['section'],
        content_key: key,
        content_value: value,
        is_active: true,
        display_order: 0,
      });
    }
    toast({ title: "Conteúdo salvo!" });
  };

  if (contentLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
    );
  }

  // Direct section mode — used by NossaHistoriaManager main tabs
  if (section === "hero")       return <HeroEditor       content={content} onSave={upsave} />;
  if (section === "story")      return <StoryEditor      content={content} onSave={upsave} />;
  if (section === "principles") return <PrinciplesEditor content={content} onSave={upsave} />;
  if (section === "cta")        return <CTAEditor />;

  return null;
}
